import React from 'react';
import { FormattedMessage } from 'react-intl';
import { cx } from '@emotion/css';
import { ProfileData } from './profile-types';
import { ProductOwnerType } from '../../../../graphql/order/models/get-orders.models';
import { useStyles } from './profile-label.styles';
import { getColor } from '../../../../utils/kids/get-color';
import { ReactComponent as ProfileIcon } from '../../../../assets/images/profileIcon.svg';
import { ReactComponent as ProfileDeletedIcon } from '../../../../assets/images/profileDeletedIcon.svg';

interface ProfileLabelProps {
  profile: ProfileData;
  className?: string;
  isDeleted: boolean;
}

export const ProfileLabel = ({ profile, className, isDeleted = false }: ProfileLabelProps): JSX.Element => {
  const classes = useStyles();
  const statusTextKey = isDeleted
    ? 'profile.status.deleted'
    : profile.isActive
      ? null
      : 'profile.status.inactive';
  const initial = profile?.firstName?.[0];
  const background = getColor((profile?.colorIndex as number) ?? 0);

  if (profile && !isDeleted && profile.ownerType === ProductOwnerType.consumer) {
    return (
      <div className={cx(classes.root, className)} data-testid="profile-label-consumer">
        <div className={classes.consumerLabel}>
          <ProfileIcon className={classes.consumerIcon} />
          <span className={classes.name}>
            <FormattedMessage id="myOrders.profile.myLenses" />
          </span>
        </div>
      </div>
    );
  }

  const testId = `profile-label-${
    isDeleted ? 'deleted' : !profile.isActive ? 'inactive' : 'dependent'
  }`;

  return (
    <div className={cx(classes.root, className)} data-testid={testId}>
      {isDeleted ? (
        <ProfileDeletedIcon className={classes.iconDeleted} />
      ) : (
        <div
          className={cx(classes.icon, { [classes.iconDeactivated]: !profile.isActive })}
          style={profile.isActive ? { background } : undefined}
        >
          {initial}
        </div>
      )}
      <div className={classes.labelText}>
        <span className={classes.name}>{profile?.firstName?.trim()}</span>
        {statusTextKey && (
          <span className={classes.status}>
            <FormattedMessage id={statusTextKey} />
          </span>
        )}
      </div>
    </div>
  );
};
